import { createFileRoute, Link } from "@tanstack/react-router";
import { useInfiniteQuery } from "@tanstack/react-query";
import { eventsApi } from "@/api/events";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { PageHeader } from "@/components/page-header";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { EmptyState } from "@/components/empty-state";
import { formatRelative } from "@/lib/format";
import { deriveSearchFrom } from "@/lib/event-search-window";
import { POLL } from "@/lib/poll";
import { Activity, Search } from "lucide-react";
import { useState, useEffect, useRef } from "react";

export const Route = createFileRoute("/_authed/events/")({
  component: EventsPage,
});

function EventsPage() {
  const [nameFilter, setNameFilter] = useState("");
  const [search, setSearch] = useState("");
  const [searchWindow, setSearchWindow] = useState<"30d" | "90d" | "all">("30d");
  const [isActive, setIsActive] = useState(true);
  const idleTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Track user activity to switch between 3s (active) and 15s (idle) poll intervals.
  const resetIdle = () => {
    setIsActive(true);
    if (idleTimer.current) clearTimeout(idleTimer.current);
    idleTimer.current = setTimeout(() => setIsActive(false), 30_000);
  };

  useEffect(() => {
    resetIdle();
    window.addEventListener("mousemove", resetIdle);
    window.addEventListener("keydown", resetIdle);
    return () => {
      window.removeEventListener("mousemove", resetIdle);
      window.removeEventListener("keydown", resetIdle);
      if (idleTimer.current) clearTimeout(idleTimer.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const searchFrom = deriveSearchFrom(search, searchWindow);

  const q = useInfiniteQuery({
    queryKey: ["events", nameFilter, search, searchWindow],
    queryFn: ({ pageParam }) =>
      eventsApi.list({
        name: nameFilter || undefined,
        search: search || undefined,
        from: searchFrom,
        cursor: pageParam,
        limit: 50,
      }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.next_cursor || undefined,
    refetchInterval: isActive ? POLL.events : POLL.idle,
  });

  const events = q.data?.pages.flatMap((p) => p.data) ?? [];

  return (
    <div className="space-y-6">
      <PageHeader eyebrow="Monitor" title="Events" />

      <div className="flex flex-wrap gap-2">
        <Input
          placeholder="Filter by event name"
          value={nameFilter}
          onChange={(e) => setNameFilter(e.target.value)}
          className="h-9 max-w-xs font-mono text-[13px]"
        />
        <div className="relative max-w-xs flex-1">
          <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search payload…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="h-9 pl-8"
          />
        </div>
        {search && (
          <div className="flex items-center gap-2 text-[12px] text-muted-foreground">
            <span>
              Searching {searchWindow === "all" ? "all time" : `last ${searchWindow === "90d" ? "90" : "30"} days`}
            </span>
            <div className="flex gap-1">
              {(["30d", "90d", "all"] as const).map((w) => (
                <Button
                  key={w}
                  variant={searchWindow === w ? "secondary" : "ghost"}
                  size="sm"
                  className="h-7 px-2 text-[11px]"
                  onClick={() => setSearchWindow(w)}
                >
                  {w === "all" ? "All" : w}
                </Button>
              ))}
            </div>
          </div>
        )}
      </div>

      <Card className="overflow-hidden">
        {events.length === 0 && !q.isLoading ? (
          <EmptyState
            icon={<Activity size={32} />}
            title="No events yet"
            description="Send an event via the SDK or /api/v1/events to see it here."
          />
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead className="pl-5 font-mono text-[11px] uppercase tracking-wider">Name</TableHead>
                <TableHead className="font-mono text-[11px] uppercase tracking-wider">Event ID</TableHead>
                <TableHead className="pr-5 text-right font-mono text-[11px] uppercase tracking-wider">Received</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {events.map((e) => (
                <TableRow key={e.id} className="group cursor-pointer">
                  <TableCell className="pl-5">
                    <Link to="/events/$id" params={{ id: e.id }}
                      className="font-mono text-[13px] font-medium text-foreground group-hover:text-brand">
                      {e.name}
                    </Link>
                  </TableCell>
                  <TableCell className="font-mono text-[12px] text-muted-foreground">{e.id}</TableCell>
                  <TableCell className="tabular pr-5 text-right font-mono text-[13px] text-muted-foreground">
                    {formatRelative(e.received_at)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
        {q.hasNextPage && (
          <div className="border-t p-4">
            <Button
              variant="outline"
              size="sm"
              onClick={() => void q.fetchNextPage()}
              disabled={q.isFetchingNextPage}
            >
              {q.isFetchingNextPage ? "Loading…" : "Load more"}
            </Button>
          </div>
        )}
      </Card>
    </div>
  );
}
